import { Button, Form, message, Select, Spin, Typography } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import Modal from "../../../../components/generic/Modal";
import { useTenantUsers } from "../../../../hooks/useTenantUsers";
import httpClient from "../../../../api/httpClient";
import MemberSelect from "../../../../components/shared/MemberSelect";
import UserItem from "../../../../components/shared/UserItem";
import { PERMISSION_OPTIONS } from "../../../../constant";
import { getUpdatePermissionsState } from "./helper";
import { SafetyCertificateOutlined } from "@ant-design/icons";

const PermissionModal = ({ file, isList = false }) => {
  const [showModal, setShowModal] = useState(false);
  const [workBookDetails, setWorkBookDetails] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [form] = Form.useForm();

  const { users, selectedTenantId } = useTenantUsers({
    fetchUsers: false,
  });

  const selectedUserId = Form.useWatch("user_id", form);

  useEffect(() => {
    if (!showModal || workBookDetails || !file?.id) return;

    (async () => {
      try {
        const { data } = await httpClient.get("spreadsheet/file-folder", {
          params: {
            type: "workbookDetails",
            workbook_id: file.id,
            tenant_id: selectedTenantId,
          },
        });

        setWorkBookDetails(data);
      } catch (error) {
        console.error("Failed to fetch workbook details:", error);
      }
    })();
  }, [showModal, workBookDetails, file?.id]);

  const usersWithDisabled = useMemo(() => {
    const memberIds = new Set(
      workBookDetails?.permissions?.map((m) => m.user_id),
    );

    return (
      users?.map((user) => ({
        ...user,
        disabled: memberIds.has(user.id),
      })) || []
    );
  }, [users, workBookDetails]);

  const onSubmit = useCallback(
    async ({ role, user_id, permission_type = "add" }) => {
      try {
        setSubmitting(true);

        await httpClient.post("spreadsheet/manage", {
          type: "permission",
          workbook_id: workBookDetails?.id,
          tenant_id: selectedTenantId,
          role,
          permission_type,
          user_id,
        });

        setWorkBookDetails(
          getUpdatePermissionsState({
            permission_type,
            user_id,
            role,
            prev: workBookDetails,
            users,
          }),
        );

        if (permission_type === "add") {
          form.resetFields();
        }
        message.success("Permission update successfully");
      } catch (error) {
        console.error("Permission update failed:", error);
      } finally {
        setSubmitting(false);
      }
    },
    [workBookDetails, selectedTenantId, users],
  );

  return (
    <>
      <div
        className="flex"
        onClick={(e) => {
          e.stopPropagation();
          setShowModal(true);
        }}
      >
        {isList ? (
          <Button
            icon={<SafetyCertificateOutlined />}
            color="primary"
            variant="dashed"
          />
        ) : (
          "Manage Permission"
        )}
      </div>
      {showModal && (
        <Modal
          isProcessing={submitting}
          title={`Share Workbook : "${file?.name}"`}
          onCancel={() => {
            if (selectedUserId) form.resetFields(["user_id"]);
            else setShowModal(false);
          }}
          width={600}
          onOk={() => form.submit()}
          open
          footer={selectedUserId ? undefined : null}
        >
          <Form
            form={form}
            layout="vertical"
            disabled={submitting}
            onFinish={onSubmit}
          >
            <Form.Item
              label="Add User"
              name="user_id"
              rules={[
                {
                  required: true,
                  message: "Please select a user",
                },
              ]}
            >
              <MemberSelect
                users={usersWithDisabled}
                //@ts-ignore
                mode="single"
                placeholder="Select User"
              />
            </Form.Item>

            {selectedUserId && (
              <Form.Item
                label="Role"
                name="role"
                rules={[
                  {
                    required: true,
                    message: "Please select a role",
                  },
                ]}
              >
                <Select
                  placeholder="Select Role"
                  options={PERMISSION_OPTIONS}
                />
              </Form.Item>
            )}

            {!workBookDetails ? (
              <div className="flex-center">
                <Spin />
              </div>
            ) : (
              !selectedUserId && (
                <>
                  <Typography.Title
                    level={5}
                    type="secondary"
                    className="fm-root-label"
                  >
                    Users with access
                  </Typography.Title>
                  {workBookDetails?.permissions?.map((user) => (
                    <UserItem user={user}>
                      <Select
                        placeholder="Select Timezone"
                        defaultValue={user?.role}
                        options={[
                          ...PERMISSION_OPTIONS,
                          {
                            label: "Remove Access",
                            value: "remove",
                          },
                        ]}
                        disabled={user?.role === "owner" || submitting}
                        variant="borderless"
                        loading={submitting}
                        onChange={(value) => {
                          const isRemove = value === "remove";

                          onSubmit({
                            user_id: user?.user_id,
                            role: isRemove ? "editor" : value,
                            permission_type: isRemove ? "remove" : "update",
                          });
                        }}
                      />
                    </UserItem>
                  ))}
                </>
              )
            )}
          </Form>
        </Modal>
      )}
    </>
  );
};

export default PermissionModal;
